import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { Calendar, Download } from 'lucide-react';
import ReportHeader from '@/components/ReportHeader';
import { apiService } from '@/services/api';
import { useFilialSelecionada } from '@/hooks/useFilialSelecionada';

const Container = styled.div`
  padding: 16px;
`;

const Filtros = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px;
`;

const Campo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 12px;
  color: #6b7280;
`;

const Input = styled.input`
  height: 36px;
  padding: 0 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
`;

const CheckboxRow = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
`;

const Acoes = styled.div`
  margin-left: auto;
  display: flex;
  gap: 8px;
`;

const Botao = styled.button`
  height: 36px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #d1d5db;
  background: #f9fafb;
  color: #111827;
  border-radius: 6px;
  cursor: pointer;
  &:hover { background: #f3f4f6; }
  &:disabled { opacity: .6; cursor: not-allowed; }
`;

const Tabela = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px;
  background: #f3f4f6;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 10px;
  border-bottom: 1px solid #f1f5f9;
`;

const ScrollTop = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  height: 16px;
  margin: 0 16px 8px 16px;
`;

const ScrollBottom = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
`;

const RelatorioTelemarketingPneusVendas: React.FC = () => {
  const { filialSelecionada } = useFilialSelecionada();
  const [dataInicio, setDataInicio] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [dataFim, setDataFim] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [todasFiliais, setTodasFiliais] = useState<boolean>(true);
  const topScrollRef = useRef<HTMLDivElement | null>(null);
  const bottomScrollRef = useRef<HTMLDivElement | null>(null);
  const [contentWidth, setContentWidth] = useState<number>(0);

  const queryKey = useMemo(() => (
    ['relatorio-telemarketing-pneus-vendas', todasFiliais ? 'todas' : (filialSelecionada?.id || '-'), dataInicio, dataFim]
  ), [todasFiliais, filialSelecionada?.id, dataInicio, dataFim]);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey,
    enabled: todasFiliais || !!filialSelecionada?.id,
    queryFn: async () => {
      if (todasFiliais) return apiService.obterTelemarketingPneusVendasTodas({ dataInicio, dataFim });
      if (!filialSelecionada?.id) return null;
      return apiService.obterTelemarketingPneusVendasFilial(filialSelecionada.id, { dataInicio, dataFim });
    }
  });

  const colunas: string[] = data?.data?.colunas || [
    'Filial',
    'VendedorTelemarketing',
    'CotacaoTelemarketing',
    'AutoEstoque',
    'CotacaoFilial',
    'NumeroVenda',
    'DataVenda',
    'TotalVenda'
  ];
  const linhas: any[] = data?.data?.dados || [];

  const formatarDataBR = (valor: any): string => {
    if (!valor) return '';
    const s = String(valor);
    const match = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) return `${match[3]}/${match[2]}/${match[1]}`;
    return s;
  };

  const formatarNumero2Casas = (valor: any): string => {
    const num = typeof valor === 'number' ? valor : parseFloat(String(valor).replace(',', '.'));
    if (isNaN(num)) return String(valor ?? '');
    return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  useEffect(() => {
    const bottom = bottomScrollRef.current;
    if (!bottom) return;
    const update = () => setContentWidth(bottom.scrollWidth);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [linhas, colunas]);

  const handleTopScroll = () => {
    const top = topScrollRef.current;
    const bottom = bottomScrollRef.current;
    if (top && bottom && bottom.scrollLeft !== top.scrollLeft) {
      bottom.scrollLeft = top.scrollLeft;
    }
  };

  const handleBottomScroll = () => {
    const top = topScrollRef.current;
    const bottom = bottomScrollRef.current;
    if (top && bottom && top.scrollLeft !== bottom.scrollLeft) {
      top.scrollLeft = bottom.scrollLeft;
    }
  };

  const handleExportar = () => {
    if (!colunas || colunas.length === 0 || linhas.length === 0) return;
    const sep = ';';
    const toCsvValue = (coluna: string, valor: any): string => {
      const raw = coluna === 'DataVenda'
        ? formatarDataBR(valor)
        : (coluna === 'TotalVenda' ? formatarNumero2Casas(valor) : String(valor ?? ''));
      const needsQuote = raw.includes('"') || raw.includes(sep) || /[\n\r]/.test(raw);
      const escaped = raw.replace(/"/g, '""');
      return needsQuote ? `"${escaped}"` : escaped;
    };

    const header = colunas.join(sep);
    const body = linhas.map((linha) => colunas.map((c) => toCsvValue(c, linha[c])).join(sep)).join('\n');
    const contexto = todasFiliais ? 'todas' : (filialSelecionada?.nome?.replace(/[^\w\-\s]/g, '') || 'filial');
    const nomeArquivo = `relatorio_telemarketing_pneus_${contexto}_${dataInicio}_a_${dataFim}.csv`;

    const csvContent = `\ufeff${header}\n${body}`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = nomeArquivo;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Container>
      <ReportHeader
        title="Telemarketing x Vendas de Pneus"
        subtitle={`${data?.data?.totalRegistros ?? 0} registros no período`}
        rightActions={(
          <Acoes>
            <Botao onClick={handleExportar} disabled={isLoading || isFetching || linhas.length === 0}>
              <Download size={16} /> Exportar
            </Botao>
            <Botao onClick={() => refetch()} disabled={isLoading || isFetching}>
              <Calendar size={16} /> Aplicar
            </Botao>
          </Acoes>
        )}
      />

      <Filtros>
        <Campo>
          <Label>Data Início</Label>
          <Input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} />
        </Campo>
        <Campo>
          <Label>Data Fim</Label>
          <Input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} />
        </Campo>
        <Campo>
          <Label>&nbsp;</Label>
          <CheckboxRow>
            <input
              type="checkbox"
              checked={todasFiliais}
              onChange={(e) => setTodasFiliais(e.target.checked)}
            />
            Todas as Filiais
          </CheckboxRow>
        </Campo>
      </Filtros>

      <ScrollTop ref={topScrollRef} onScroll={handleTopScroll}>
        <div style={{ width: contentWidth }} />
      </ScrollTop>

      <ScrollBottom ref={bottomScrollRef} onScroll={handleBottomScroll}>
        <Tabela>
          <thead>
            <tr>
              {colunas.map((c) => (
                <Th key={c}>{c}</Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><Td colSpan={colunas.length}>Carregando...</Td></tr>
            ) : linhas.length === 0 ? (
              <tr><Td colSpan={colunas.length}>Nenhum registro encontrado</Td></tr>
            ) : (
              linhas.map((linha, idx) => (
                <tr key={idx}>
                  {colunas.map((c) => (
                    <Td key={c}>
                      {c === 'DataVenda'
                        ? formatarDataBR(linha[c])
                        : c === 'TotalVenda'
                          ? formatarNumero2Casas(linha[c])
                          : String(linha[c] ?? '')}
                    </Td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </Tabela>
      </ScrollBottom>
    </Container>
  );
};

export default RelatorioTelemarketingPneusVendas;
